import React, { useState } from 'react';
import { useAppState } from '../../context/AppStateProvider';
import { Bookmark, Plus, Folder, ExternalLink, Trash2, GripVertical } from 'lucide-react';
import SearchBar from '../shared/SearchBar';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableItem = ({ item, onDelete, setSelectedItem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="item-card-sortable" data-testid={`bookmark-item-${item.id}`}>
      <div className="drag-handle" {...attributes} {...listeners}>
        <GripVertical size={16} />
      </div>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="item-link"
        onClick={() => setSelectedItem(item)}
      >
        <div className="item-header">
          <span className="item-title">{item.title}</span>
          <ExternalLink size={14} className="external-icon" />
        </div>
        <div className="item-url">{new URL(item.url).hostname}</div>
      </a>
      <button
        className="item-delete-btn"
        data-testid={`delete-item-${item.id}`}
        onClick={(e) => {
          e.preventDefault();
          onDelete(item.id);
        }}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const WorkspaceBookmarks = () => {
  const { folders, items, addFolder, deleteFolder, deleteItem, setSelectedItem, searchQuery } = useAppState();
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [sortedItems, setSortedItems] = useState([]);

  const bookmarkFolders = folders.filter(f => f.category === 'bookmarks');
  const bookmarkItems = items.filter(i => i.category === 'bookmarks');

  const filteredItems = searchQuery 
    ? bookmarkItems.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.url.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : bookmarkItems;

  // Group items
  const ungroupedItems = filteredItems.filter(i => !i.folderId);

  React.useEffect(() => {
    setSortedItems(ungroupedItems);
  }, [ungroupedItems.length]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSortedItems((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleAddFolder = () => {
    if (newFolderName.trim()) {
      addFolder(newFolderName.trim(), 'bookmarks');
      setNewFolderName('');
      setShowAddFolder(false);
    }
  };

  return (
    <div className="workspace-container" data-testid="workspace-bookmarks">
      <div className="workspace-header">
        <div className="workspace-header-left">
          <Bookmark className="workspace-icon" size={24} />
          <div>
            <h2 className="workspace-title">Bookmarks</h2>
            <p className="workspace-subtitle">{filteredItems.length} items</p>
          </div>
        </div>
        <button 
          className="add-folder-btn"
          data-testid="add-folder-btn"
          onClick={() => setShowAddFolder(!showAddFolder)}
        >
          <Plus size={18} />
          Add Folder
        </button>
      </div>

      <SearchBar placeholder="Search bookmarks..." />

      {showAddFolder && (
        <div className="add-folder-form" data-testid="add-folder-form">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Folder name"
            className="folder-input"
            data-testid="folder-name-input"
            onKeyPress={(e) => e.key === 'Enter' && handleAddFolder()}
          />
          <button onClick={handleAddFolder} className="save-btn" data-testid="save-folder-btn">Save</button>
          <button onClick={() => setShowAddFolder(false)} className="cancel-btn" data-testid="cancel-folder-btn">Cancel</button>
        </div>
      )}

      <div className="content-area">
        {bookmarkFolders.length === 0 && filteredItems.filter(i => !i.folderId).length === 0 ? (
          <div className="empty-state-large" data-testid="empty-state">
            <Bookmark size={48} className="empty-icon" />
            <p>No bookmarks yet</p>
            <p className="empty-hint">Use Quick Capture to add your first bookmark</p>
          </div>
        ) : (
          <>
            {/* Ungrouped items */}
            {sortedItems.length > 0 && (
              <div className="items-section">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortedItems.map(i => i.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="items-grid-sortable">
                      {sortedItems.map(item => (
                        <SortableItem
                          key={item.id}
                          item={item}
                          onDelete={deleteItem}
                          setSelectedItem={setSelectedItem}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}

            {/* Folders */}
            {bookmarkFolders.map(folder => {
              const folderItems = filteredItems.filter(i => i.folderId === folder.id);
              if (searchQuery && folderItems.length === 0) return null;
              
              return (
                <div key={folder.id} className="folder-section" data-testid={`folder-${folder.id}`}>
                  <div className="folder-header">
                    <Folder size={18} className="folder-icon" />
                    <h3 className="folder-title">{folder.name}</h3>
                    <span className="folder-count">({folderItems.length})</span>
                    <button
                      className="folder-delete-btn"
                      data-testid={`delete-folder-${folder.id}`}
                      onClick={() => deleteFolder(folder.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={folderItems.map(i => i.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-grid-sortable">
                        {folderItems.map(item => (
                          <SortableItem
                            key={item.id}
                            item={item}
                            onDelete={deleteItem}
                            setSelectedItem={setSelectedItem}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default WorkspaceBookmarks;
